import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // Information about the currently logged in user
  const [error, setError] = useState(null); // Error message
  const [profile, setProfile] = useState(null); // The user's profile
  const [isLoading, setIsLoading] = useState(true); // Loading state

  // Fetch the currently logged in user's profile
  const fetchProfile = async (userId) => {
    const { data, error } = await supabase.from("users").select("*").eq("id", userId).single();

    // Check for errors
    if (error) {
      setError(error?.message);
      return;
    }

    // Set the profile
    setProfile(data);
  };

  // Checks current session on app load
  useEffect(() => {
    // Get current session
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      // Set the current user, if there is one else null
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      // If there is a current user, fetch their profile
      if (currentUser) {
        fetchProfile(currentUser.id);
      }
      // Set loading to false
      setIsLoading(false);
    };

    // Run the function
    getSession();

    // Auth listener to listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Set the current user, if there is one else null
      const currentUser = session?.user ?? null;
      setUser(currentUser);

      // If there is a current user, fetch their profile
      if (currentUser) {
        fetchProfile(currentUser.id);
      } else {
        setProfile(null);
      }

      // Set loading to false
      setIsLoading(false);
    });

    // Return the cleanup function
    return () => subscription.unsubscribe();
  }, []);

  // Signs the user out
  const signOut = async () => {
    // Instantly update UI
    setUser(null);
    setProfile(null);

    const { error } = await supabase.auth.signOut();

    if (error) {
      setError(error?.message);
    }
  };

  const isAuthenticated = !!user; // Check if the user is authenticated
  const isAdmin = profile?.role === "admin"; // Check if the user is an admin

  return (
    <AuthContext.Provider
      value={{
        user,
        error,
        profile,
        isLoading,
        signOut,
        isAuthenticated,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
